/**
 * Hook for managing analytics data, report loading, and time series calculations
 *
 * Composes the analytics domain hooks:
 * - useAnalyticsReports: report fetching with incremental time-range caching
 * - useAnalyticsSummary: memoized summary metrics and distributions
 * - useAnalyticsGraphs: graph state and KPI dataset management
 *
 * This hook owns the organization/time filter state, valid-KPI tracking, and
 * the graph time-series pipeline.
 */
import { useState, useEffect, useCallback } from 'react';

import Util from '../util/util.js';
import { getOrganizationAccess } from '../util/storage';
import {
  getStartAndEndTimes,
  getBucketStartTimes,
  getDateLabels,
  getTimeSeries,
} from '../util/analytics/queryUtils';
import { VALID_HROS, getTimeFilterOptions } from '../util/analytics/constants';
import { populateTimeSeries } from '../util/analytics/dataTransformers';
import { useErrorState } from '../util/error';

import useAnalyticsGraphs from './useAnalyticsGraphs';
import useAnalyticsReports from './useAnalyticsReports';
import useAnalyticsSummary from './useAnalyticsSummary';

const { DateTime } = require('luxon');

// Re-export for backward compatibility
export { getTimeFilterOptions };

/**
 * Get valid KPIs for an organization
 */
const fetchValidKPIs = async (organization) => {
  const requestData = {
    queryParams: {
      organization,
      value_type: 'KPI',
    },
  };
  try {
    const data = await Util.get(
      'Resty',
      '/items/organization_configuration/value_type',
      requestData,
    );
    return data.items;
  } catch (error) {
    Util.printAPIError(error);
    return [];
  }
};

/**
 * Main analytics data hook
 */
export default function useAnalyticsData(organization, noLogin, initialOrgFilterValue = null) {
  // Error state
  const { error, hasError, handleError, clearError } = useErrorState({
    context: 'AnalyticsData',
    showToasts: true,
  });

  // KPI state
  const [validKPIs, setValidKPIs] = useState([]);

  // Use extracted graphs hook - destructure to get stable function references
  const graphsHook = useAnalyticsGraphs();
  const { setGraphStates, updateKPIColors, KPIColors } = graphsHook;

  // Organization filter state
  const [OrgFilterOpen, setOrgFilterOpen] = useState(false);
  const [OrgFilterValue, setOrgFilterValue] = useState(initialOrgFilterValue || [organization]);
  const [PreviousOrgFilterValue, setPreviousOrgFilterValue] = useState([]);
  const [validOrgs, setValidOrgs] = useState([]);
  const [OrgFilter, setOrgFilter] = useState([organization]);
  const [loadingOrg, setLoadingOrg] = useState(true);

  // Time filter state
  const [activeFilter, setActiveFilter] = useState(3); // Default to "Past 6 months"

  // Report fetching with incremental caching
  const { allReports, reportsLoading, analyticsCutoffDates, fetchReports } = useAnalyticsReports(
    organization,
    { OrgFilterValue, activeFilter, handleError }
  );

  // Memoized summary metrics and distributions
  const { filteredReportsData, summaryMetrics, hroDistribution, kpiDistribution } =
    useAnalyticsSummary(allReports, activeFilter, analyticsCutoffDates);

  /**
   * Update valid organizations from storage
   */
  const updateValidOrgs = useCallback(async () => {
    let organization_access = await getOrganizationAccess();
    organization_access = organization_access.length ? organization_access : [organization];
    setValidOrgs(organization_access);
    setOrgFilter(
      organization_access.map((x) => ({ label: x, value: x })),
    );
    setLoadingOrg(false);
  }, [organization]);

  /**
   * Update valid KPIs
   */
  const updateValidKPIs = useCallback(async (kpis = []) => {
    if (kpis.length === 0) {
      kpis = (await fetchValidKPIs(organization)).map((value) => value['value']);
    }
    const kpiSet = new Set(kpis);
    allReports.forEach((report) => {
      kpiSet.add(report.KPI_AI);
    });
    setValidKPIs(Array.from(kpiSet));
    updateKPIColors(kpiSet.size);
  }, [organization, allReports, updateKPIColors]);

  /**
   * Populate time series data for graphs (summary metrics now computed via useMemo)
   */
  const populateGraphTimeSeries = useCallback((data, bucketStartTimes, timeSeries, timeAmount, dateLabels, kpis, colors) => {
    data.forEach((report) => {
      populateTimeSeries(bucketStartTimes, DateTime.fromMillis(report.timestamp), report, timeSeries, timeAmount);
    });
    setGraphStates(dateLabels, timeSeries, kpis, colors);
  }, [setGraphStates]);

  /**
   * Update graph time series for the current filter (uses memoized filteredReportsData)
   */
  const updateGraphTimeSeries = useCallback(() => {
    const { data, queryMode, timeAmount, timeStride } = filteredReportsData;
    if (!data || data.length === 0 || !queryMode) return;

    const timeSeries = getTimeSeries(timeAmount, VALID_HROS.concat(validKPIs));
    const [, endTime] = getStartAndEndTimes(queryMode, timeAmount * timeStride);
    const bucketStartTimes = getBucketStartTimes(queryMode, endTime, timeStride, timeAmount);
    const dateLabels = getDateLabels(queryMode, bucketStartTimes);

    populateGraphTimeSeries(data, bucketStartTimes, timeSeries, timeAmount, dateLabels, validKPIs, KPIColors);
  }, [filteredReportsData, validKPIs, KPIColors, populateGraphTimeSeries]);

  // Update graph time series when filter or data changes
  useEffect(() => {
    if (allReports.length > 0 && validKPIs.length > 0) {
      updateGraphTimeSeries();
    }
  }, [allReports, validKPIs, updateGraphTimeSeries]);

  return {
    // Error state
    error,
    hasError,
    clearError,

    // Report data
    allReports,
    reportsLoading,

    // Summary metrics (memoized)
    submittedReports: summaryMetrics.submittedReports,
    responseTime: summaryMetrics.responseTime,
    openReports: summaryMetrics.openReports,
    closedReports: summaryMetrics.closedReports,
    urgentReports: summaryMetrics.urgentReports,

    // Distribution data (memoized)
    hroDistribution,
    kpiDistribution,

    // KPI data
    validKPIs,
    KPIColors: graphsHook.KPIColors,

    // Graph data (from graphs hook)
    allGraphStates: graphsHook.allGraphStates,
    allDerivedGraphInfo: graphsHook.allDerivedGraphInfo,
    sortedKPIDatasetDetails: graphsHook.sortedKPIDatasetDetails,
    partialKPIDatasetDetails: graphsHook.partialKPIDatasetDetails,

    // Organization filter
    OrgFilterOpen,
    setOrgFilterOpen,
    OrgFilterValue,
    setOrgFilterValue,
    PreviousOrgFilterValue,
    setPreviousOrgFilterValue,
    validOrgs,
    OrgFilter,
    setOrgFilter,
    loadingOrg,

    // Time filter
    activeFilter,
    setActiveFilter,

    // Analytics cutoff dates per org (for "clean slate" feature)
    analyticsCutoffDates,

    // Actions
    updateValidOrgs,
    updateValidKPIs,
    fetchReports,
  };
}
